import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOpponents, createOpponent, deleteOpponent } from '../services/api';

/**
 * Manages the user's opponent profiles: list, inline create, and delete.
 */
function OpponentManager() {
  const navigate = useNavigate();
  const [opponents, setOpponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Inline create form
  const [displayName, setDisplayName] = useState('');
  const [nickname, setNickname] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Delete confirmation per card
  const [confirmingId, setConfirmingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    getOpponents()
      .then(setOpponents)
      .catch(() => setError('Failed to load managers'))
      .finally(() => setLoading(false));
  }, []);

  async function handleCreate(e) {
    e.preventDefault();
    if (!displayName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await createOpponent({
        displayName: displayName.trim(),
        nickname: nickname.trim() || undefined,
      });
      setOpponents(prev => [...prev, created]);
      setDisplayName('');
      setNickname('');
    } catch (err) {
      setCreateError(err.response?.data?.error?.message || 'Failed to create manager');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id) {
    setDeletingId(id);
    try {
      await deleteOpponent(id);
      setOpponents(prev => prev.filter(o => o.id !== id));
      setConfirmingId(null);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to delete manager');
    } finally {
      setDeletingId(null);
    }
  }

  if (loading) {
    return (
      <div className='loading'>
        <div className='spinner' />
        <p>Loading managers...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <h2 style={{ marginTop: 0 }}>Managers</h2>

      {error && <div className='error-banner' style={{ marginBottom: 16 }}>{error}</div>}

      {/* Inline create form */}
      <div className='card' style={{ marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 12px' }}>Add Manager</h3>
        <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            className='input'
            style={{ width: '100%' }}
            placeholder='Name (required)'
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            required
          />
          <input
            className='input'
            style={{ width: '100%' }}
            placeholder='Nickname (optional)'
            value={nickname}
            onChange={e => setNickname(e.target.value)}
          />
          {createError && <div className='error-banner'>{createError}</div>}
          <div>
            <button className='btn btn-primary' type='submit' disabled={creating || !displayName.trim()}>
              {creating ? 'Adding…' : 'Add Manager'}
            </button>
          </div>
        </form>
      </div>

      {/* Opponent cards */}
      {opponents.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>No managers yet. Add one above.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {opponents.map(o => (
            <div key={o.id} className='draft-list-item'>
              <div className='draft-list-item-header'>
                <button
                  className='btn-link'
                  style={{ fontWeight: 600, fontSize: '1rem', textAlign: 'left' }}
                  onClick={() => navigate(`/opponents/${o.id}`)}
                >
                  {o.displayName}
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {confirmingId !== o.id ? (
                    <button
                      className='filter-pill'
                      style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                      onClick={() => setConfirmingId(o.id)}
                    >
                      ×
                    </button>
                  ) : (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}>
                      <span style={{ color: 'var(--text-muted)' }}>Delete?</span>
                      <button
                        className='filter-pill active'
                        style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                        onClick={() => handleDelete(o.id)}
                        disabled={deletingId === o.id}
                      >
                        {deletingId === o.id ? '…' : 'Yes'}
                      </button>
                      <button
                        className='filter-pill'
                        style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                        onClick={() => setConfirmingId(null)}
                        disabled={deletingId === o.id}
                      >
                        No
                      </button>
                    </span>
                  )}
                </div>
              </div>
              <div className='draft-list-item-meta'>
                {o.nickname && <span>{o.nickname}</span>}
                {o.notes && (
                  <span style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {o.notes}
                  </span>
                )}
                <span>
                  {o.draftCount ?? 0} drafts
                  {o.mockDraftCount > 0 && (
                    <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>(+{o.mockDraftCount} mock)</span>
                  )}
                </span>
                <span>{o.leagueCount ?? 0} leagues</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default OpponentManager;
